import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Button, ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import NavLink from './common/NavLink';
import useLeagueData from './useLeagueData';
import { slugify } from '../utils/slugify'; 
import useTeamCompetitionData from './useTeamCompetitionData';

const LeagueTables = () => {
  const { id } = useParams();
  console.log('League ID from useParams:', id);

  const { leagueData, loading: leagueLoading, error: leagueError } = useLeagueData(id);
  const { teams, matches, loading: teamsLoading, error: teamsError } = useTeamCompetitionData(id);

  const [selectedTeams, setSelectedTeams] = useState(new Set());
  const [tableFilter, setTableFilter] = useState('all');
  const [formMatches, setFormMatches] = useState(5);
  const [partialTable, setPartialTable] = useState(null);

  console.log('leagueLoading:', leagueLoading, 'teamsLoading:', teamsLoading);

  if (leagueLoading || teamsLoading) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">Ładowanie...</Typography>
      </Box>
    );
  }

  if (leagueError || teamsError) {
    return (
      <Box sx={{ textAlign: 'center', padding: '20px' }}>
        <Typography variant="h6">{leagueError || teamsError}</Typography>
      </Box>
    );
  }

  const calculateTeamStats = (matchesToConsider, filter) => {
    if (!teams || teams.length === 0) {
      console.log('No teams available');
      return [];
    }

    const teamStats = teams.map(team => ({
      teamId: team.teamId,
      teamName: team.teamName,
      points: 0,
      matchesPlayed: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      goalsScored: 0,
      goalsConceded: 0,
    }));

    const teamStatsMap = new Map(teamStats.map(stat => [stat.teamId, stat]));

    if (!matchesToConsider || matchesToConsider.length === 0) {
      console.log('No matches available');
      return Array.from(teamStatsMap.values()).sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        const aGoalDiff = a.goalsScored - a.goalsConceded;
        const bGoalDiff = b.goalsScored - b.goalsConceded;
        if (bGoalDiff !== aGoalDiff) return bGoalDiff - aGoalDiff;
        return b.goalsScored - a.goalsScored;
      });
    }

    matchesToConsider.forEach(match => {
      const isMatchEnded = match.status == 'ended'
      if (!isMatchEnded) return;

	  const homeTeamId = match.homeTeamId;
      const awayTeamId = match.awayTeamId;
      const homeGoals = match.homeGoals;
      const awayGoals = match.awayGoals;

      const homeTeamStats = teamStatsMap.get(homeTeamId);
      const awayTeamStats = teamStatsMap.get(awayTeamId);

      if (!homeTeamStats || !awayTeamStats) return;

      let includeMatch = false;
      if (filter === 'all') {
        includeMatch = true;
      } else if (filter === 'home' && match.homeTeamId) {
        includeMatch = true;
      } else if (filter === 'away' && match.awayTeamId) {
        includeMatch = true;
      } else if (filter === 'form') {
        const teamMatches = matchesToConsider
          .filter(m => (m.homeTeamId === homeTeamId || m.awayTeamId === homeTeamId || m.homeTeamId === awayTeamId || m.awayTeamId === awayTeamId) && m.status == 'ended')
          .sort((a, b) => {
            const dateA = a.matchDate ? new Date(a.matchDate) : new Date(0);
            const dateB = b.matchDate ? new Date(b.matchDate) : new Date(0);
            return dateB - dateA;
          });
        const homeTeamRecentMatches = teamMatches.filter(m => m.homeTeamId === homeTeamId || m.awayTeamId === homeTeamId).slice(0, formMatches);
        const awayTeamRecentMatches = teamMatches.filter(m => m.homeTeamId === awayTeamId || m.awayTeamId === awayTeamId).slice(0, formMatches);
        includeMatch = homeTeamRecentMatches.includes(match) || awayTeamRecentMatches.includes(match);
      }

      if (!includeMatch) return;

      if (filter === 'all' || filter === 'home' || (filter === 'form' && (match.homeTeamId === homeTeamId || match.awayTeamId === homeTeamId))) {
        homeTeamStats.matchesPlayed += 1;
        homeTeamStats.goalsScored += homeGoals;
        homeTeamStats.goalsConceded += awayGoals;

        if (homeGoals > awayGoals) {
          homeTeamStats.points += 3;
          homeTeamStats.wins += 1;
        } else if (homeGoals === awayGoals) {
          homeTeamStats.points += 1;
          homeTeamStats.draws += 1;
        } else {
          homeTeamStats.losses += 1;
        }
      }

      if (filter === 'all' || filter === 'away' || (filter === 'form' && (match.homeTeamId === awayTeamId || match.awayTeamId === awayTeamId))) {
        awayTeamStats.matchesPlayed += 1;
        awayTeamStats.goalsScored += awayGoals;
        awayTeamStats.goalsConceded += homeGoals;

        if (awayGoals > homeGoals) {
          awayTeamStats.points += 3;
          awayTeamStats.wins += 1;
        } else if (awayGoals === homeGoals) {
          awayTeamStats.points += 1;
          awayTeamStats.draws += 1;
        } else {
          awayTeamStats.losses += 1;
        }
      }
    });

    return Array.from(teamStatsMap.values()).sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      const aGoalDiff = a.goalsScored - a.goalsConceded;
      const bGoalDiff = b.goalsScored - b.goalsConceded;
      if (bGoalDiff !== aGoalDiff) return bGoalDiff - aGoalDiff;
      return b.goalsScored - a.goalsScored;
    });
  };

  const teamStats = calculateTeamStats(matches, tableFilter);

  const calculatePartialTable = () => {
    if (selectedTeams.size < 2) {
      setPartialTable(null);
      return;
    }

    const filteredMatches = matches.filter(match => 
      selectedTeams.has(match.homeTeamId) && selectedTeams.has(match.awayTeamId)
    );

    const partialStats = calculateTeamStats(filteredMatches, 'all');
    setPartialTable(partialStats.filter(stat => selectedTeams.has(stat.teamId)));
  };

  const handleTeamSelect = (teamId) => {
    const newSelectedTeams = new Set(selectedTeams);
    if (newSelectedTeams.has(teamId)) {
      newSelectedTeams.delete(teamId);
    } else {
      newSelectedTeams.add(teamId);
    }
    setSelectedTeams(newSelectedTeams);
  };

  const handleFilterChange = (event, newFilter) => {
    if (newFilter) {
      setTableFilter(newFilter);
    }
  };

  const renderTable = (stats, isPartial = false) => {
    if (!stats || stats.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', padding: '10px' }}>
          <Typography variant="h6">Brak danych do wyświetlenia.</Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ backgroundColor: '#2c2c2c', width: 'fit-content', margin: '0 auto' }}>
        <Table sx={{ width: 'auto' }} aria-label={isPartial ? "partial league table" : "league table"}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px' }}>#</TableCell>
              {!isPartial && (
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px' }}></TableCell>
              )}
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px' }}>Drużyna</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px', textAlign: 'center' }}>M</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px', textAlign: 'center' }}>Pkt</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px', textAlign: 'center' }}>Z</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px', textAlign: 'center' }}>R</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px', textAlign: 'center' }}>P</TableCell>
              <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', padding: '2px 10px', textAlign: 'center' }}>Br</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stats.map((team, index) => {
              const promotionSpots = leagueData?.promotion_spots || 0;
              const promotionPlayoffSpots = leagueData?.promotion_playoff_spots || 0;
              const relegationSpots = leagueData?.relegation_spots || 0;
              const relegationPlayoffSpots = leagueData?.relegation_playoff_spots || 0;

              let backgroundColor = 'transparent';
              {/*if (!isPartial) {
                if (index < promotionSpots) {
                  backgroundColor = 'rgba(46, 125, 50, 0.3)';
                } else if (index < promotionSpots + promotionPlayoffSpots) {
                  backgroundColor = 'rgba(150, 200, 80, 0.2)';
                } else if (index >= stats.length - relegationSpots) {
                  backgroundColor = 'rgba(211, 47, 47, 0.2)';
                } else if (index >= stats.length - (relegationSpots + relegationPlayoffSpots) && index < stats.length - relegationSpots) {
                  backgroundColor = 'rgba(255, 152, 0, 0.2)';
                }
              }*/}

              return (
                <TableRow
                  key={team.teamId}
                  sx={{
                    backgroundColor,
                    '&:hover': { backgroundColor: backgroundColor === 'transparent' ? '#3a3a3a' : backgroundColor },
                  }}
                >
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '2px 10px', textAlign: 'center' }}>{index + 1}</TableCell>
                  {!isPartial && (
                    <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '0px 10px' }}>
                      <Checkbox
                        checked={selectedTeams.has(team.teamId)}
                        onChange={() => handleTeamSelect(team.teamId)}
                        sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ffffff' } }}
                      />
                    </TableCell>
                  )}
                  <TableCell component={NavLink}
					to={`/team/${team.teamId}/${slugify(team.teamName)}`} sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '2px 10px' }}>{team.teamName}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '2px 10px', textAlign: 'center' }}>{team.matchesPlayed}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '2px 10px', textAlign: 'center' }}>{team.points}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '2px 10px', textAlign: 'center' }}>{team.wins}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '2px 10px', textAlign: 'center' }}>{team.draws}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '2px 10px', textAlign: 'center' }}>{team.losses}</TableCell>
                  <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', padding: '2px 10px', textAlign: 'center' }}>
                    {team.goalsScored}:{team.goalsConceded}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ padding: { xs: '20px', md: '40px' } }}>
      <Typography variant="h5" sx={{ textAlign: { xs: 'center', sm: 'center' }, padding: '10px 10px', fontSize: { xs: '1.0rem', sm: '1.2rem' } }}>
        Aktualna tabela - {leagueData?.name || 'Liga'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={tableFilter}
          exclusive
          onChange={handleFilterChange}
          aria-label="table filter"
          sx={{ backgroundColor: '#2c2c2c' }}
        >
          <ToggleButton value="all" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#3a3a3a', color: '#ffffff' } }}>
            Wszystkie
          </ToggleButton>
          <ToggleButton value="home" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#3a3a3a', color: '#ffffff' } }}>
            Dom
          </ToggleButton>
          <ToggleButton value="away" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#3a3a3a', color: '#ffffff' } }}>
            Wyjazd
          </ToggleButton>
          {/*<ToggleButton value="form" sx={{ color: '#ffffff', '&.Mui-selected': { backgroundColor: '#3a3a3a', color: '#ffffff' } }}>
            Forma
          </ToggleButton>*/}
        </ToggleButtonGroup>
{/* 
        {tableFilter === 'form' && (
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel sx={{ color: '#ffffff' }}>Ostatnie mecze</InputLabel>
            <Select
              value={formMatches}
              onChange={(e) => setFormMatches(e.target.value)}
              sx={{
                color: '#ffffff',
                backgroundColor: '#2c2c2c',
                '& .MuiSvgIcon-root': { color: '#ffffff' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
              }}
              label="Ostatnie mecze"
            >
              {[3, 5, 7, 10].map(num => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )} */}
      </Box>

      {renderTable(teamStats)}

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="contained"
          onClick={calculatePartialTable}
          disabled={selectedTeams.size < 2}
          sx={{
            backgroundColor: selectedTeams.size < 2 ? '#555' : '#1976d2',
            '&:hover': { backgroundColor: selectedTeams.size < 2 ? '#555' : '#1565c0' },
          }}
        >
          Tabela częściowa
        </Button>
      </Box>

      {partialTable && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
            Tabela częściowa (mecze między wybranymi drużynami)
          </Typography>
          {renderTable(partialTable, true)}
        </>
      )}

    </Box>
  );
};

export default LeagueTables;